<template>
  <div class="palette-wrap">
    <div
      v-for="cat in categories"
      :key="cat.label"
      class="palette-category"
    >
      <div class="cat-label">{{ cat.label }}</div>
      <div class="cat-grid">
        <button
          v-for="item in cat.items"
          :key="item.type"
          class="palette-card"
          type="button"
          @click="addBlock(item.type)"
        >
          <q-icon :name="item.icon" size="20px" />
          <span class="palette-card-label">{{ item.label }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ContentBlock } from 'src/stores/useAdminCoursesStore';

const emit = defineEmits<{ (e: 'add', block: ContentBlock): void }>();

const categories = [
  {
    label: 'Texto',
    items: [
      { type: 'paragraph', label: 'Párrafo', icon: 'notes' },
      { type: 'heading', label: 'Título', icon: 'title' },
      { type: 'list', label: 'Lista', icon: 'format_list_bulleted' },
      { type: 'quote', label: 'Cita', icon: 'format_quote' },
      { type: 'code', label: 'Código', icon: 'code' },
    ],
  },
  {
    label: 'Destacado',
    items: [
      { type: 'callout', label: 'Aviso', icon: 'info' },
      { type: 'key_points', label: 'Puntos clave', icon: 'bolt' },
    ],
  },
  {
    label: 'Visual',
    items: [
      { type: 'image', label: 'Imagen', icon: 'image' },
      { type: 'grid', label: 'Grilla de cards', icon: 'grid_view' },
      { type: 'comparison', label: 'Comparativa', icon: 'compare_arrows' },
    ],
  },
  {
    label: 'Interactivo',
    items: [
      { type: 'toggle', label: 'Desplegable', icon: 'expand_more' },
      { type: 'quiz', label: 'Quiz', icon: 'quiz' },
    ],
  },
  {
    label: 'Estructura',
    items: [
      { type: 'divider', label: 'Separador', icon: 'horizontal_rule' },
    ],
  },
];

// Default block shapes per type
function makeDefaultBlock(type: string): ContentBlock {
  switch (type) {
    case 'heading':
      return { type: 'heading', level: 2, text: '' };
    case 'paragraph':
      return { type: 'paragraph', markdown: '' };
    case 'list':
      return { type: 'list', style: 'bullet', items: [''] };
    case 'callout':
      return { type: 'callout', variant: 'info', title: '', text: '' };
    case 'quote':
      return { type: 'quote', text: '', attribution: '' };
    case 'code':
      return { type: 'code', language: '', code: '' };
    case 'image':
      return { type: 'image', src: '', alt: '', caption: '' };
    case 'toggle':
      return { type: 'toggle', title: '', content: '' };
    case 'key_points':
      return { type: 'key_points', title: '', points: [''] };
    case 'grid':
      return {
        type: 'grid',
        columns: 3,
        cards: [{ icon: '', title: '', body: '', accent: '' }],
      };
    case 'comparison':
      return {
        type: 'comparison',
        title: '',
        correct: { label: 'Correcto', items: [''] },
        incorrect: { label: 'Incorrecto', items: [''] },
      };
    case 'quiz':
      return {
        type: 'quiz',
        questions: [
          { question: '', options: ['', ''], correctIndex: 0, explanation: '' },
        ],
      };
    case 'divider':
      return { type: 'divider' };
    default:
      // Rama inalcanzable: todos los tipos de la paleta tienen case explicito.
      throw new Error('Tipo de bloque desconocido: ' + type);
  }
}

function addBlock(type: string) {
  emit('add', makeDefaultBlock(type));
}
</script>

<style scoped lang="scss">
.palette-wrap {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
}

.palette-category {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.cat-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--oa-text-muted);
}

.cat-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.palette-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px 8px;
  border: 1px solid var(--oa-border);
  border-radius: 8px;
  background: var(--oa-bg-2);
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  color: var(--oa-text-primary);

  &:hover {
    border-color: var(--color-ocean-light);
    background: var(--oa-active-bg);
    color: var(--color-ocean-light);
  }
}

.palette-card-label {
  font-size: 12px;
  font-weight: 500;
  text-align: center;
  line-height: 1.2;
}
</style>
